import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, GripVertical, ChevronUp, ChevronDown, Copy, Save,
  Refrigerator, Package, LayoutGrid, Minus, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { DndContext, PointerSensor, useSensor, useSensors, closestCorners, pointerWithin, useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).slice(2));
const UNIT_TYPES = [
  { v: "fridge", label: "Fridge", icon: Refrigerator },
  { v: "shelf", label: "Shelf", icon: LayoutGrid },
  { v: "ambient", label: "Ambient", icon: Package },
];
const typeIcon = (t) => (UNIT_TYPES.find((x) => x.v === t)?.icon || LayoutGrid);

function ItemCard({ item, onQty, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.uid });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const tid = item.sku || item.product_id;
  return (
    <div ref={setNodeRef} style={style} data-testid={`plano-item-${tid}`} className="flex-1 min-w-[240px] max-w-full flex items-center gap-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 p-2">
      <button {...attributes} {...listeners} data-testid={`plano-item-handle-${tid}`} className="shrink-0 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none p-0.5"><GripVertical className="h-4 w-4" /></button>
      <div className="h-14 w-14 shrink-0 rounded-md bg-slate-50 dark:bg-slate-800 overflow-hidden grid place-items-center">
        {item.image_url ? <img src={resolveUrl(item.image_url)} alt="" className="h-full w-full object-contain" /> : <Package className="h-5 w-5 text-slate-300" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate" title={item.name}>{item.name}</p>
        <p className="text-xs text-slate-400 truncate">{item.sku || "—"}{item.pack_size > 1 ? ` · ${item.pack_size}/pack` : ""}</p>
        <div className="mt-1 flex items-center gap-1">
          <button onClick={() => onQty(-1)} data-testid={`plano-qty-dec-${tid}`} className="h-6 w-6 grid place-items-center rounded border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><Minus className="h-3 w-3" /></button>
          <span data-testid={`plano-qty-${tid}`} title="Units when fully stocked" className="w-7 text-center text-sm font-semibold tabular-nums">{item.qty}</span>
          <button onClick={() => onQty(1)} data-testid={`plano-qty-inc-${tid}`} className="h-6 w-6 grid place-items-center rounded border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><Plus className="h-3 w-3" /></button>
          <span className="text-[10px] text-slate-400 ml-1">full-stock</span>
        </div>
      </div>
      <button onClick={onRemove} data-testid={`plano-item-remove-${tid}`} title="Remove from level" className="shrink-0 self-start text-slate-400 hover:text-rose-600 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function LevelBox({ unitIdx, levelIdx, level, ops, levelNumber }) {
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `container:${level.id}` });
  const { attributes, listeners, setNodeRef: setSortRef, transform, transition, isDragging } = useSortable({ id: `level:${level.id}` });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 20 : undefined };
  return (
    <div ref={setSortRef} style={style} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-2 space-y-2" data-testid={`plano-level-${level.id}`}>
      <div className="flex items-center gap-1.5">
        <button {...attributes} {...listeners} data-testid={`plano-level-handle-${level.id}`} title="Drag to reorder level" className="shrink-0 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none p-0.5"><GripVertical className="h-3.5 w-3.5" /></button>
        <span className="text-[10px] font-bold text-slate-400 shrink-0 w-5">L{levelNumber}</span>
        <Input data-testid={`plano-level-name-${level.id}`} value={level.name} onChange={(e) => ops.renameLevel(unitIdx, levelIdx, e.target.value)} placeholder="e.g. Healthy Snacks, Drinks etc." className="h-7 text-xs font-medium flex-1" />
        <button data-testid={`plano-level-add-product-${level.id}`} onClick={() => ops.openAdd(level.id)} className="text-xs text-indigo-600 hover:underline flex items-center gap-0.5 shrink-0"><Plus className="h-3 w-3" /> Product</button>
        <button onClick={() => ops.moveLevel(unitIdx, levelIdx, 1)} title="Move up" className="text-slate-400 hover:text-slate-700 p-0.5"><ChevronUp className="h-3.5 w-3.5" /></button>
        <button onClick={() => ops.moveLevel(unitIdx, levelIdx, -1)} title="Move down" className="text-slate-400 hover:text-slate-700 p-0.5"><ChevronDown className="h-3.5 w-3.5" /></button>
        <button data-testid={`plano-level-delete-${level.id}`} onClick={() => ops.deleteLevel(unitIdx, levelIdx)} className="text-slate-400 hover:text-rose-600 p-0.5"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <SortableContext items={level.items.map((i) => i.uid)} strategy={rectSortingStrategy}>
        <div ref={setDropRef} className={`flex flex-wrap gap-2 min-h-[76px] rounded-md p-1 transition-colors ${isOver ? "bg-indigo-50 dark:bg-indigo-950/30 ring-1 ring-indigo-300" : ""}`}>
          {level.items.map((it) => (
            <ItemCard key={it.uid} item={it} onQty={(d) => ops.qty(level.id, it.uid, d)} onRemove={() => ops.removeItem(level.id, it.uid)} />
          ))}
          {!level.items.length && <p className="w-full text-[11px] text-slate-400 text-center py-3">Drag products here or click “+ Product”.</p>}
        </div>
      </SortableContext>
    </div>
  );
}

function TrayCard({ item }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `tray-${item.product_id}` });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const tid = item.sku || item.product_id;
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} data-testid={`plano-tray-item-${tid}`} className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 p-2 cursor-grab active:cursor-grabbing touch-none min-w-[200px] max-w-full">
      <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
      <div className="h-10 w-10 shrink-0 rounded-md bg-slate-50 dark:bg-slate-800 overflow-hidden grid place-items-center">
        {item.image_url ? <img src={resolveUrl(item.image_url)} alt="" className="h-full w-full object-contain" /> : <Package className="h-4 w-4 text-slate-300" />}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate" title={item.name}>{item.name}</p>
        <p className="text-xs text-slate-400 truncate">{item.sku || "—"}</p>
      </div>
    </div>
  );
}

function TrayBox({ items }) {
  const { setNodeRef, isOver } = useDroppable({ id: "container:__tray__" });
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-amber-50/40 dark:bg-amber-950/10 p-3" data-testid="plano-tray">
      <div className="flex items-center gap-1.5 mb-2">
        <Package className="h-4 w-4 text-amber-600 shrink-0" />
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Waiting to be assigned</p>
        <span className="text-xs text-slate-400">· {items.length} item(s)</span>
      </div>
      <SortableContext items={items.map((i) => `tray-${i.product_id}`)} strategy={rectSortingStrategy}>
        <div ref={setNodeRef} className={`flex flex-wrap gap-2 min-h-[64px] rounded-md p-1 transition-colors ${isOver ? "bg-amber-100/60 dark:bg-amber-950/30 ring-1 ring-amber-300" : ""}`} data-testid="plano-tray-dropzone">
          {items.map((it) => <TrayCard key={it.product_id} item={it} />)}
          {!items.length && <p className="w-full text-[11px] text-slate-400 text-center py-3">Every product assigned to this kiosk is placed on a shelf — nothing waiting.</p>}
        </div>
      </SortableContext>
    </div>
  );
}

function UnitLevels({ unit, unitIdx, ops }) {
  const { setNodeRef, isOver } = useDroppable({ id: `unit:${unit.id}` });
  const rev = unit.levels.map((l, li) => ({ l, li })).slice().reverse();
  return (
    <div ref={setNodeRef} className={`p-3 flex flex-col gap-3 min-h-[60px] transition-colors ${isOver ? "bg-indigo-50/60 dark:bg-indigo-950/20 ring-1 ring-inset ring-indigo-300" : ""}`} data-testid={`plano-unit-drop-${unit.id}`}>
      <SortableContext items={rev.map((x) => `level:${x.l.id}`)} strategy={verticalListSortingStrategy}>
        {rev.map(({ l, li }) => <LevelBox key={l.id} unitIdx={unitIdx} levelIdx={li} level={l} ops={ops} levelNumber={li + 1} />)}
      </SortableContext>
      {!unit.levels.length && <p className="text-xs text-slate-400 p-2">No levels — add one, or drag a level here.</p>}
    </div>
  );
}

export default function PlanogramEditor({ kioskId }) {
  const [units, setUnits] = useState([]);
  const [kioskProducts, setKioskProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [kiosks, setKiosks] = useState([]);
  const [addTarget, setAddTarget] = useState(null);
  const [addQuery, setAddQuery] = useState("");
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyForm, setCopyForm] = useState({ source: "", products: true, planogram: true });
  const [copying, setCopying] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  // Prefer the droppable the pointer is actually inside. When dragging a level, prefer level/unit
  // targets (enables cross-unit moves); when dragging an item, ignore unit drop-zones so items land
  // on a level. Fall back to closestCorners for reordering.
  const collisionDetection = (args) => {
    const aid = String(args.active.id);
    const pointer = pointerWithin(args);
    if (aid.startsWith("level:")) {
      const levels = pointer.filter((c) => String(c.id).startsWith("level:") && String(c.id) !== aid);
      if (levels.length) return levels;
      const unitZones = pointer.filter((c) => String(c.id).startsWith("unit:"));
      if (unitZones.length) return unitZones;
      return pointer.length ? pointer : closestCorners(args);
    }
    const notUnit = pointer.filter((c) => !String(c.id).startsWith("unit:"));
    return notUnit.length ? notUnit : closestCorners(args);
  };

  useEffect(() => {
    api.get("/products").then((r) => setAllProducts(r.data.filter((p) => !p.archived && !p.deleted)));
    api.get("/kiosks").then((r) => setKiosks(r.data)).catch(() => {});
  }, []);

  const load = () => {
    if (!kioskId) { setUnits([]); setKioskProducts([]); return; }
    setLoading(true);
    Promise.all([
      api.get(`/kiosks/${kioskId}/planogram`),
      api.get(`/kiosk-products?kiosk_id=${kioskId}`),
    ])
      .then(([pr, kr]) => {
        setUnits((pr.data.units || []).map((u) => ({ ...u, levels: u.levels.map((l) => ({ ...l, items: l.items.map((i) => ({ ...i, uid: uid() })) })) })));
        setKioskProducts((kr.data || [])
          .filter((kp) => kp.product && !kp.product.archived && !kp.product.deleted)
          .map((kp) => ({ product_id: kp.product_id, name: kp.product.name, sku: kp.product.sku, image_url: kp.product.image_url || "", pack_size: kp.product.pack_size || 1 })));
      })
      .catch(() => { setUnits([]); setKioskProducts([]); })
      .finally(() => { setDirty(false); setLoading(false); });
  };
  useEffect(() => { load(); }, [kioskId]); // eslint-disable-line

  const mutate = (fn) => setUnits((prev) => { const next = structuredClone(prev); fn(next); return next; });
  const touch = () => setDirty(true);

  const ops = {
    renameLevel: (ui, li, name) => { mutate((n) => { n[ui].levels[li].name = name; }); touch(); },
    moveLevel: (ui, li, dir) => { const j = li + dir; if (j < 0) return; mutate((n) => { const ls = n[ui].levels; if (j >= ls.length) return; [ls[li], ls[j]] = [ls[j], ls[li]]; }); touch(); },
    deleteLevel: (ui, li) => { mutate((n) => { n[ui].levels.splice(li, 1); }); touch(); },
    openAdd: (levelId) => { setAddTarget(levelId); setAddQuery(""); },
    qty: (levelId, u, d) => { mutate((n) => { for (const un of n) for (const l of un.levels) if (l.id === levelId) for (const it of l.items) if (it.uid === u) it.qty = Math.max(0, (it.qty || 0) + d); }); touch(); },
    removeItem: (levelId, u) => { mutate((n) => { for (const un of n) for (const l of un.levels) if (l.id === levelId) l.items = l.items.filter((it) => it.uid !== u); }); touch(); },
  };

  const addUnit = () => { mutate((n) => n.push({ id: uid(), name: "New unit", type: "shelf", levels: [{ id: uid(), name: "", items: [] }] })); touch(); };
  const renameUnit = (ui, name) => { mutate((n) => { n[ui].name = name; }); touch(); };
  const setUnitType = (ui, t) => { mutate((n) => { n[ui].type = t; }); touch(); };
  const moveUnit = (ui, dir) => { const j = ui + dir; if (j < 0) return; mutate((n) => { if (j >= n.length) return; [n[ui], n[j]] = [n[j], n[ui]]; }); touch(); };
  const deleteUnit = (ui) => { mutate((n) => n.splice(ui, 1)); touch(); };
  const addLevel = (ui) => { mutate((n) => n[ui].levels.push({ id: uid(), name: "", items: [] })); touch(); };

  const addProduct = (p) => {
    mutate((n) => {
      for (const un of n) for (const l of un.levels) if (l.id === addTarget) l.items.push({ uid: uid(), product_id: p.id, qty: 1, name: p.name, sku: p.sku, image_url: p.image_url, pack_size: p.pack_size || 1 });
    });
    touch();
    setAddTarget(null);
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    const fromTray = activeId.startsWith("tray-");

    // Move a whole level — reorder within a unit or across units. Level numbers auto-update from position.
    if (activeId.startsWith("level:")) {
      const srcLevelId = activeId.slice(6);
      let tgtLevelId = null, tgtUnitId = null;
      if (overId.startsWith("level:")) tgtLevelId = overId.slice(6);
      else if (overId.startsWith("container:")) tgtLevelId = overId.slice(10);
      else if (overId.startsWith("unit:")) tgtUnitId = overId.slice(5);
      else for (const u of units) for (const l of u.levels) if (l.items.some((i) => i.uid === over.id)) tgtLevelId = l.id;
      if (tgtLevelId === srcLevelId) return;
      if (!tgtLevelId && !tgtUnitId) return;
      mutate((n) => {
        let srcUnit, srcIdx = -1;
        for (const u of n) { const idx = u.levels.findIndex((l) => l.id === srcLevelId); if (idx >= 0) { srcUnit = u; srcIdx = idx; } }
        if (!srcUnit) return;
        let destUnit, destIdx = null;
        if (tgtLevelId) {
          for (const u of n) { const idx = u.levels.findIndex((l) => l.id === tgtLevelId); if (idx >= 0) { destUnit = u; destIdx = idx; } }
        } else if (tgtUnitId) {
          destUnit = n.find((u) => u.id === tgtUnitId);
          destIdx = destUnit ? destUnit.levels.length : null; // append (bottom of that unit)
        }
        if (!destUnit || destIdx == null) return;
        if (destUnit === srcUnit) {
          srcUnit.levels = arrayMove(srcUnit.levels, srcIdx, destIdx);
        } else {
          const [moved] = srcUnit.levels.splice(srcIdx, 1);
          destUnit.levels.splice(Math.min(destIdx, destUnit.levels.length), 0, moved);
        }
      });
      touch();
      return;
    }

    // Resolve destination container
    let destId = null, destIdx = null;
    if (overId.startsWith("container:")) destId = overId.slice(10);
    else if (overId.startsWith("level:")) destId = overId.slice(6);
    else if (overId.startsWith("tray-")) destId = "__tray__";
    else for (const u of units) for (const l of u.levels) { const idx = l.items.findIndex((i) => i.uid === over.id); if (idx >= 0) { destId = l.id; destIdx = idx; } }
    if (!destId) return;

    // Drop into the waiting tray → just take it off the shelf (stays assigned to kiosk)
    if (destId === "__tray__") {
      if (fromTray) return;
      mutate((n) => { for (const u of n) for (const l of u.levels) { const idx = l.items.findIndex((i) => i.uid === active.id); if (idx >= 0) l.items.splice(idx, 1); } });
      touch();
      return;
    }

    // Drop a waiting product onto a shelf
    if (fromTray) {
      const pid = activeId.slice(5);
      const info = kioskProducts.find((p) => p.product_id === pid);
      if (!info) return;
      mutate((n) => {
        for (const u of n) for (const l of u.levels) if (l.id === destId) {
          const card = { uid: uid(), product_id: pid, qty: 1, name: info.name, sku: info.sku, image_url: info.image_url, pack_size: info.pack_size || 1 };
          if (destIdx == null || destIdx < 0) l.items.push(card); else l.items.splice(destIdx, 0, card);
        }
      });
      touch();
      return;
    }

    // Move an existing shelf item between/within levels
    mutate((n) => {
      let src, srcIdx;
      for (const u of n) for (const l of u.levels) { const idx = l.items.findIndex((i) => i.uid === active.id); if (idx >= 0) { src = l; srcIdx = idx; } }
      if (!src) return;
      let dest;
      for (const u of n) for (const l of u.levels) if (l.id === destId) dest = l;
      if (!dest) return;
      const [moved] = src.items.splice(srcIdx, 1);
      if (destIdx == null || destIdx < 0) dest.items.push(moved);
      else dest.items.splice(destIdx, 0, moved);
    });
    touch();
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { units: units.map((u) => ({ id: u.id, name: u.name, type: u.type, levels: u.levels.map((l) => ({ id: l.id, name: l.name, items: l.items.map((i) => ({ product_id: i.product_id, qty: i.qty })) })) })) };
      await api.put(`/kiosks/${kioskId}/planogram`, payload);
      toast.success("Planogram saved — this now sets the kiosk screen order");
      setDirty(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doCopy = async () => {
    if (!copyForm.source) return;
    setCopying(true);
    try {
      const r = await api.post(`/kiosks/${kioskId}/copy-from`, { source_kiosk_id: copyForm.source, include_products: copyForm.products, include_planogram: copyForm.planogram });
      toast.success(`Copied ${r.data.copied_products} product(s) & planogram`);
      setCopyOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCopying(false); }
  };

  const q = addQuery.trim().toLowerCase();
  const addMatches = allProducts.filter((p) => !q || `${p.name} ${p.sku || ""}`.toLowerCase().includes(q)).slice(0, 40);
  const totalItems = units.reduce((s, u) => s + u.levels.reduce((a, l) => a + l.items.length, 0), 0);
  const placedIds = new Set();
  units.forEach((u) => u.levels.forEach((l) => l.items.forEach((it) => placedIds.add(it.product_id))));
  const trayItems = kioskProducts.filter((p) => !placedIds.has(p.product_id));

  if (!kioskId) return <p className="text-slate-400 text-sm">Select a kiosk to edit its planogram.</p>;

  return (
    <div className="space-y-4" data-testid="plano-editor">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-xs text-slate-400 mr-auto">{units.length} unit(s) · {totalItems} placement(s) · {trayItems.length} waiting · <span className="text-slate-500">top-to-bottom is the kiosk order (top shelf shows first · L1 is the bottom shelf)</span></p>
        <Button data-testid="plano-copy-open" variant="outline" size="sm" onClick={() => { setCopyForm({ source: "", products: true, planogram: true }); setCopyOpen(true); }}><Copy className="h-4 w-4 mr-1.5" /> Copy from…</Button>
        <Button data-testid="plano-save" size="sm" onClick={save} disabled={saving || !dirty} className="bg-indigo-600 hover:bg-indigo-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save{dirty ? " *" : ""}</>}</Button>
      </div>

      {loading && <p className="text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading planogram…</p>}

      {!loading && (
        <div className="rounded-lg border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/60 dark:bg-indigo-950/30 px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300 leading-relaxed" data-testid="plano-help">
          <p className="font-semibold text-indigo-700 dark:text-indigo-300 flex items-center gap-1.5"><LayoutGrid className="h-3.5 w-3.5" /> How the planogram works</p>
          <p className="mt-1">This is the <b>shelf layout</b> — where each product sits and how many it holds when <b>fully stocked</b>. The number on each tile is that <b>full-stock capacity</b>, used to build restock/picking lists. It is <b>not</b> the live stock count — to see or change how many units are in the kiosk right now, use the <b>Products</b> tab.</p>
        </div>
      )}

      {!loading && (
        <>
          <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragEnd={onDragEnd}>
          <div className="space-y-4">
            <TrayBox items={trayItems} />
            {units.map((u, ui) => {
              const Icon = typeIcon(u.type);
              return (
                <div key={u.id} data-testid={`plano-unit-${u.id}`} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-gradient-to-r from-indigo-50 to-white dark:from-slate-900 dark:to-slate-950 border-b border-slate-100 dark:border-slate-800">
                    <Icon className="h-4 w-4 text-indigo-600 shrink-0" />
                    <Input data-testid={`plano-unit-name-${u.id}`} value={u.name} onChange={(e) => renameUnit(ui, e.target.value)} className="h-8 font-semibold max-w-[200px]" />
                    <Select value={u.type} onValueChange={(v) => setUnitType(ui, v)}>
                      <SelectTrigger data-testid={`plano-unit-type-${u.id}`} className="h-8 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>{UNIT_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="ml-auto flex items-center gap-1">
                      <button data-testid={`plano-unit-add-level-${u.id}`} onClick={() => addLevel(ui)} className="text-xs text-indigo-600 hover:underline flex items-center gap-0.5"><Plus className="h-3 w-3" /> Level</button>
                      <button onClick={() => moveUnit(ui, -1)} className="text-slate-400 hover:text-slate-700 p-1"><ChevronUp className="h-4 w-4" /></button>
                      <button onClick={() => moveUnit(ui, 1)} className="text-slate-400 hover:text-slate-700 p-1"><ChevronDown className="h-4 w-4" /></button>
                      <button data-testid={`plano-unit-delete-${u.id}`} onClick={() => deleteUnit(ui)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <UnitLevels unit={u} unitIdx={ui} ops={ops} />
                </div>
              );
            })}
          </div>
          </DndContext>
          <Button data-testid="plano-add-unit" variant="outline" onClick={addUnit} className="w-full border-dashed"><Plus className="h-4 w-4 mr-1.5" /> Add unit (fridge / shelf / ambient)</Button>
        </>
      )}

      <Dialog open={!!addTarget} onOpenChange={(o) => !o && setAddTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add a product</DialogTitle><DialogDescription>Pick a product to place on this level. You can set its full-stock quantity after adding.</DialogDescription></DialogHeader>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="plano-add-search" autoFocus value={addQuery} onChange={(e) => setAddQuery(e.target.value)} placeholder="Search by name or SKU…" className="pl-8" />
          </div>
          <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800" data-testid="plano-add-results">
            {addMatches.map((p) => (
              <button key={p.id} data-testid={`plano-add-opt-${p.sku || p.id}`} onClick={() => addProduct(p)} className="flex items-center gap-2 w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800">
                {p.image_url ? <img src={resolveUrl(p.image_url)} alt="" className="h-8 w-8 rounded object-cover" /> : <div className="h-8 w-8 rounded bg-slate-100 dark:bg-slate-800" />}
                <span className="text-sm flex-1 truncate">{p.name}</span>
                {p.sku && <span className="text-xs text-slate-400">{p.sku}</span>}
              </button>
            ))}
            {!addMatches.length && <p className="px-3 py-3 text-sm text-slate-400">No matching products.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Copy from another kiosk</DialogTitle><DialogDescription>Clone the product line-up and/or planogram layout from an existing kiosk into this one. Stock quantities are not copied.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Source kiosk</label>
              <Select value={copyForm.source} onValueChange={(v) => setCopyForm({ ...copyForm, source: v })}>
                <SelectTrigger data-testid="plano-copy-source" className="mt-1"><SelectValue placeholder="Choose a kiosk to copy from" /></SelectTrigger>
                <SelectContent>{kiosks.filter((k) => k.id !== kioskId).map((k) => <SelectItem key={k.id} value={k.id}>{k.name} ({k.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="plano-copy-products" checked={copyForm.products} onChange={(e) => setCopyForm({ ...copyForm, products: e.target.checked })} /> Copy product assignments &amp; PAR</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="plano-copy-planogram" checked={copyForm.planogram} onChange={(e) => setCopyForm({ ...copyForm, planogram: e.target.checked })} /> Copy planogram layout &amp; order</label>
            <p className="text-xs text-amber-600 dark:text-amber-400">This overwrites the current planogram for this kiosk.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCopyOpen(false)}>Cancel</Button>
            <Button data-testid="plano-copy-confirm" onClick={doCopy} disabled={!copyForm.source || copying} className="bg-indigo-600 hover:bg-indigo-700">{copying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Copy"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
